import { type ScopedProjectRef, type ScopedThreadRef, type ThreadId } from "@cmmd-forge/contracts";
import { selectEnvironmentState, type AppState, type EnvironmentState } from "./store";
import { type Project, type SidebarThreadSummary, type Thread, type ThreadShell } from "./types";
import { getThreadFromEnvironmentState } from "./threadDerivation";

export function createProjectSelectorByRef(
  ref: ScopedProjectRef | null | undefined,
): (state: AppState) => Project | undefined {
  return (state) =>
    ref ? selectEnvironmentState(state, ref.environmentId).projectById[ref.projectId] : undefined;
}

function createScopedThreadSelector(
  resolveRef: (state: AppState) => ScopedThreadRef | null | undefined,
): (state: AppState) => Thread | undefined {
  let previousEnvironmentState: EnvironmentState | undefined;
  let previousThreadId: ThreadId | undefined;
  let previousThread: Thread | undefined;

  return (state) => {
    const ref = resolveRef(state);
    if (!ref) {
      return undefined;
    }

    const environmentState = selectEnvironmentState(state, ref.environmentId);
    if (
      previousThread &&
      previousEnvironmentState === environmentState &&
      previousThreadId === ref.threadId
    ) {
      return previousThread;
    }

    previousEnvironmentState = environmentState;
    previousThreadId = ref.threadId;
    previousThread = getThreadFromEnvironmentState(environmentState, ref.threadId);
    return previousThread;
  };
}

export function createThreadSelectorByRef(
  ref: ScopedThreadRef | null | undefined,
): (state: AppState) => Thread | undefined {
  return createScopedThreadSelector(() => ref);
}

export function createThreadSubscriptionErrorSelector(
  ref: ScopedThreadRef | null | undefined,
): (state: AppState) => string | undefined {
  return (state) => {
    if (!ref) return undefined;
    const environmentState = selectEnvironmentState(state, ref.environmentId);
    return environmentState.threadSubscriptionErrorById[ref.threadId];
  };
}

export interface ThreadTeam {
  readonly you: ThreadShell;
  readonly master: ThreadShell | undefined;
  readonly workers: readonly ThreadShell[];
}

/**
 * Return the team (master + ordered workers) the referenced thread belongs to.
 *
 * - If `ref` points to a master: `you === master`, `workers` are all children.
 * - If `ref` points to a worker: `master` is the parent, `workers` are siblings
 *   (including `you`).
 * - If `ref` points to a worker whose master is not loaded, `master` is
 *   `undefined` but siblings still resolve (they share the same parent id).
 * - Returns `undefined` when the thread itself is not loaded.
 *
 * Workers are sorted by `createdAt` ascending for stable roster ordering.
 */
export function selectThreadTeamByRef(
  ref: ScopedThreadRef | null | undefined,
): (state: AppState) => ThreadTeam | undefined {
  // Cache the last-returned team so repeated calls with an unchanged
  // `threadShellById` reference return the SAME object. This is required by
  // `useSyncExternalStore` (and therefore Zustand's `useStore`): if the
  // snapshot selector returns a freshly-built object on every call, React
  // bails out into the "getSnapshot should be cached" infinite-loop path.
  let prevThreadShellById: EnvironmentState["threadShellById"] | undefined;
  let prevYou: ThreadShell | undefined;
  let prevMaster: ThreadShell | undefined;
  let prevTeam: ThreadTeam | undefined;

  return (state) => {
    if (!ref) {
      prevTeam = undefined;
      prevThreadShellById = undefined;
      prevYou = undefined;
      prevMaster = undefined;
      return undefined;
    }
    const environmentState = selectEnvironmentState(state, ref.environmentId);
    const shellsById = environmentState.threadShellById;
    const you = shellsById[ref.threadId];
    if (!you) {
      prevTeam = undefined;
      prevThreadShellById = undefined;
      prevYou = undefined;
      prevMaster = undefined;
      return undefined;
    }

    const masterId = you.kind === "master" ? you.id : you.parentThreadId;
    const master = masterId !== null && masterId !== undefined ? shellsById[masterId] : undefined;

    if (
      prevTeam &&
      prevThreadShellById === shellsById &&
      prevYou === you &&
      prevMaster === master
    ) {
      return prevTeam;
    }

    const workers: ThreadShell[] = [];
    if (masterId !== null && masterId !== undefined) {
      for (const shell of Object.values(shellsById)) {
        if (shell.kind === "worker" && shell.parentThreadId === masterId) {
          workers.push(shell);
        }
      }
      workers.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }

    prevThreadShellById = shellsById;
    prevYou = you;
    prevMaster = master;
    prevTeam = { you, master, workers };
    return prevTeam;
  };
}

interface ThreadScopeCandidate {
  readonly ref: ScopedThreadRef;
  readonly active: boolean;
  readonly bootstrapped: boolean;
}

/**
 * Names the durable Environment id a legacy runtime id belongs to, or null when the caller
 * cannot tell. Only the catalog knows, so a caller that holds it passes this in.
 */
export type DurableScopeResolver = (
  legacyEnvironmentId: ScopedThreadRef["environmentId"],
) => string | null;

/**
 * Picks the scope a thread settles on when several hold it. A bootstrapped scope beats a
 * detail-only one, and the active scope beats the rest. One Environment can hold the thread
 * under its legacy runtime id and its durable id at once. The durable scope is where the
 * connection registers, so a legacy scope is set aside when the durable scope of the same
 * Environment is bootstrapped too, even if the legacy scope is the active one.
 */
function pickThreadScope(
  candidates: readonly ThreadScopeCandidate[],
  durableScopeOf: DurableScopeResolver | undefined,
): ScopedThreadRef | undefined {
  const bootstrappedIds = new Set(
    candidates
      .filter((candidate) => candidate.bootstrapped)
      .map((c) => String(c.ref.environmentId)),
  );
  const live = candidates.filter((candidate) => {
    const durableId = durableScopeOf?.(candidate.ref.environmentId) ?? null;
    return durableId === null || !bootstrappedIds.has(durableId);
  });
  const bootstrapped = live.filter((candidate) => candidate.bootstrapped);
  const winner =
    bootstrapped.find((candidate) => candidate.active) ??
    bootstrapped.at(-1) ??
    live.find((candidate) => candidate.active) ??
    live[0];
  return winner?.ref;
}

/**
 * Finds a thread by id in whichever Environment scope holds it, for a route that names a
 * thread before its scope is known. Pass `durableScopeOf` to let a durable scope supersede the
 * legacy scope of the same Environment. Without it every scope is judged alone.
 */
export function createThreadSelectorAcrossEnvironments(
  threadId: ThreadId | null | undefined,
  durableScopeOf?: DurableScopeResolver,
): (state: AppState) => Thread | undefined {
  return createScopedThreadSelector((state) => {
    if (!threadId) return undefined;
    const scopes = Object.entries(state.environmentStateById) as Array<
      [ScopedThreadRef["environmentId"], EnvironmentState]
    >;
    return pickThreadScope(
      scopes
        .filter(([, environmentState]) => environmentState.threadShellById[threadId])
        .map(([environmentId, environmentState]) => ({
          ref: { environmentId, threadId },
          active: state.activeEnvironmentId === environmentId,
          bootstrapped: environmentState.bootstrapComplete,
        })),
      durableScopeOf,
    );
  });
}

/**
 * Every environment's sidebar summaries, for cross-thread surfaces such as
 * the notification coordinator.
 *
 * Caches the last-returned array on the `environmentStateById` reference
 * (every store write replaces it, see the store's set helpers), so repeated
 * calls with an unchanged map return the SAME array. This is required by
 * `useSyncExternalStore`: a freshly-built array per call sends React into
 * the "getSnapshot should be cached" infinite-loop path, exactly as
 * documented on `selectThreadTeamByRef`.
 */
let summariesEnvironmentStateById: AppState["environmentStateById"] | undefined;
let summariesCache: SidebarThreadSummary[] = [];

export function selectSidebarSummariesAcrossEnvironments(state: AppState): SidebarThreadSummary[] {
  if (
    summariesEnvironmentStateById === state.environmentStateById &&
    summariesCache !== undefined
  ) {
    return summariesCache;
  }
  summariesEnvironmentStateById = state.environmentStateById;
  summariesCache = Object.values(state.environmentStateById).flatMap((environmentState) =>
    Object.values(environmentState.sidebarThreadSummaryById),
  );
  return summariesCache;
}
